import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import JsonLd from "@/components/base/JsonLd";
import PageHero from "@/components/feature/PageHero";
import SiteLayout from "@/components/feature/SiteLayout";
import ArticleCard from "@/components/base/ArticleCard";
import FilterBar from "@/pages/daily/components/FilterBar";
import CollectionList from "@/pages/daily/components/CollectionList";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const hotTags = [
  { label: "同框回应", query: "同框" },
  { label: "定档预告", query: "定档" },
  { label: "巡演返场", query: "返场" },
  { label: "红毯造型", query: "红毯" },
  { label: "综艺救场", query: "救场" },
  { label: "私服同款", query: "私服" },
  { label: "行业复盘", query: "复盘" },
];

export default function Daily() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "";
  const keyword = searchParams.get("q") || "";

  const filtered = useMemo(() => {
    const q = keyword.trim();
    return articles.filter((item) => {
      const matchCategory = !activeCategory || item.categorySlug === activeCategory;
      const matchKeyword =
        !q ||
        item.title.includes(q) ||
        item.excerpt.includes(q) ||
        item.tags.some((tag) => tag.includes(q));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, keyword]);

  const handleCategoryChange = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug) {
      next.set("category", slug);
    } else {
      next.delete("category");
    }
    next.delete("page");
    setSearchParams(next);
  };

  const handleSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("q", value);
    } else {
      next.delete("q");
    }
    setSearchParams(next);
  };

  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? filtered : filtered.slice(0, 9);

  const activeName = categories.find((c) => c.slug === activeCategory)?.name;

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "每日合集列表",
    description:
      "瓜田日报每日合集列表页，按主题分类浏览爆料吃瓜话题，支持关键词搜索与分类筛选。",
    inLanguage: "zh-CN",
    url: "https://dearyuan.cn/daily",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: filtered.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `https://dearyuan.cn/article/${item.slug}`,
      })),
    },
  };

  return (
    <>
      <PageMeta
        title="每日合集列表｜爆料吃瓜话题分类浏览与搜索"
        description="按明星恋情、作品动态、综艺名场面、时尚造型等分类浏览爆料吃瓜话题，支持关键词搜索，快速定位你想跟进的每日合集与事件汇总。"
        keywords="爆料吃瓜每日合集,爆料吃瓜分类,娱乐话题搜索,事件汇总,热门盘点"
        canonical="/daily"
      />
      <JsonLd data={collectionSchema} />

      <SiteLayout>
        <PageHero
          eyebrow="每日合集"
          title="按分类与关键词，找到你想跟的那条线"
          desc="收录近期的每日合集内容，可以按主题分类筛选，也可以直接搜索人物或事件关键词。"
          crumbs={[{ label: "每日合集" }]}
        >
          <div className="flex gap-6 text-secondary-50">
            <div>
              <p className="font-heading text-3xl font-black">{articles.length}</p>
              <p className="mt-1 text-xs text-secondary-100/70">在库话题</p>
            </div>
            <div>
              <p className="font-heading text-3xl font-black">{categories.length}</p>
              <p className="mt-1 text-xs text-secondary-100/70">常设分类</p>
            </div>
          </div>
        </PageHero>

        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
            <div>
              <FilterBar
                activeCategory={activeCategory}
                keyword={keyword}
                resultCount={filtered.length}
                onCategoryChange={handleCategoryChange}
                onSearch={handleSearch}
              />

              <h2 className="mt-8 font-heading text-lg font-bold text-foreground-950">
                {activeName ? `${activeName} · 话题列表` : "全部话题"}
              </h2>

              {visible.length > 0 ? (
                <>
                  <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                    {visible.map((item) => (
                      <ArticleCard key={item.slug} article={item} />
                    ))}
                  </div>
                  {filtered.length > 9 && !expanded && (
                    <div className="mt-8 flex justify-center">
                      <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300/70 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-arrow-down-line text-base"></i>
                        </span>
                        加载更多话题（剩余 {filtered.length - 9} 条）
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-6 flex flex-col items-center rounded-card border border-dashed border-background-300/80 bg-background-50 px-6 py-16 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-search-eye-line text-2xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
                    没有找到匹配的话题
                  </h3>
                  <p className="mt-2 max-w-sm text-sm text-foreground-600">
                    试试更换关键词，或者切换到「全部话题」查看当前收录的所有内容。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchParams(new URLSearchParams());
                    }}
                    className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
                  >
                    重置筛选条件
                  </button>
                </div>
              )}
            </div>

            <aside className="flex flex-col gap-6">
              <CollectionList />

              <div className="rounded-card border border-background-200/80 bg-accent-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-links-line text-accent-700"></i>
                  </span>
                  热门标签
                </h2>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {hotTags.map((tag) => (
                    <li key={tag.label}>
                      <button
                        type="button"
                        onClick={() => handleSearch(tag.query)}
                        className="rounded-full border border-accent-300/70 bg-background-50 px-3 py-1 text-xs text-accent-900 transition-colors duration-200 hover:bg-accent-100"
                      >
                        {tag.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </SiteLayout>
    </>
  );
}